import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image, SafeAreaView } from 'react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import type { RootStackParamList } from '../types/navigation';
import { savePendingAppraisal } from '../lib/pendingAppraisals';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { ConditionBadge } from '../components/common/ConditionBadge';
import { DetailGroup } from '../components/common/DetailGroup';
import { DetailRow } from '../components/common/DetailRow';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'AppraiseResult'>;
  route: RouteProp<RootStackParamList, 'AppraiseResult'>;
};

/**
 * AppraiseResultScreen - Displays valuation results for unauthenticated users.
 * Uses the same UI patterns as AddAnalyzedItemScreen for consistency.
 */
export default function AppraiseResultScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        scrollView: {
          flex: 1,
        },
        formContainer: {
          padding: theme.spacing.lg,
        },
        itemCard: {
          marginBottom: theme.spacing.xl,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: theme.colors.backgroundSurface,
          overflow: 'hidden',
        },
        itemImageContainer: {
          height: 150,
          backgroundColor: theme.colors.backgroundElevated,
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        itemImage: {
          width: '100%',
          height: '100%',
          resizeMode: 'contain',
        },
        itemContent: {
          padding: theme.spacing.lg,
        },
        valueRange: {
          ...theme.typography.valueMd,
          color: theme.colors.accent,
          marginTop: theme.spacing.sm,
          marginBottom: theme.spacing.xs,
        },
        valueRangeEmpty: {
          ...theme.typography.lg,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.sm,
          marginBottom: theme.spacing.xs,
        },
        priceSourceLabel: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginBottom: theme.spacing.lg,
          letterSpacing: 0.6,
        },
        upgradePromptInline: {
          alignItems: 'center',
          marginBottom: theme.spacing.lg,
          padding: theme.spacing.md,
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        actionPromptInline: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.xs,
          marginBottom: theme.spacing.xs,
          textAlign: 'center',
        },
        upgradeDescriptionInline: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.sm,
        },
        inlineSignUpButton: {
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.lg,
        },
        marketDemandContainer: {
          marginBottom: theme.spacing.lg,
        },
        marketDemandBar: {
          height: 8,
          backgroundColor: theme.colors.backgroundElevated,
          borderRadius: theme.radius.radiusSm,
          overflow: 'hidden',
          marginTop: theme.spacing.sm,
        },
        marketDemandFill: {
          height: '100%',
          backgroundColor: theme.colors.accent,
        },
        fieldGroup: {
          marginBottom: theme.spacing.lg,
        },
        conditionWrapper: {
          marginTop: theme.spacing.sm,
        },
        selectionHeader: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          marginBottom: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        selectionCount: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        selectAllButton: {
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: 'transparent',
          marginLeft: theme.spacing.sm,
        },
        selectAllButtonActive: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        selectAllText: {
          ...theme.typography.sm,
          color: theme.colors.textPrimary,
        },
        selectAllTextActive: {
          color: theme.colors.onAccent,
        },
        actionSection: {
          marginTop: theme.spacing.xl,
          marginBottom: theme.spacing.lg,
        },
        upgradePrompt: {
          alignItems: 'center',
          marginBottom: theme.spacing.lg,
          padding: theme.spacing.lg,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        actionPrompt: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.sm,
          marginBottom: theme.spacing.sm,
          textAlign: 'center',
        },
        upgradeDescription: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
        },
        fullWidthButton: {
          width: '100%',
        },
        scanAgainButton: {
          marginTop: theme.spacing.md,
        },
        disclaimer: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          textAlign: 'center',
          marginTop: theme.spacing.xl,
          paddingHorizontal: theme.spacing.lg,
          letterSpacing: 0.6,
        },
      }),
    [theme],
  );
  const { items, imageUri, imageBase64 } = route.params;
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);

  const item = items[selectedItemIndex];

  // For unauthenticated users, we only have AI estimates (no backend market data)
  const priceSourceLabel = '🤖 AI Estimate';

  // Save appraisal and navigate to sign up/in
  const handleSignUpWithSave = async () => {
    try {
      // Save all appraised items for later (with base64 image for persistence)
      for (const appraisedItem of items) {
        await savePendingAppraisal(appraisedItem, imageUri, imageBase64);
      }
      console.log('[AppraiseResult] Saved appraisal with image, navigating to SignUp');
    } catch (error) {
      console.error('Failed to save pending appraisal:', error);
    }
    // Navigate regardless of save success
    navigation.navigate('SignUp');
  };

  const handleSignInWithSave = async () => {
    try {
      // Save all appraised items for later (with base64 image for persistence)
      for (const appraisedItem of items) {
        await savePendingAppraisal(appraisedItem, imageUri, imageBase64);
      }
      console.log('[AppraiseResult] Saved appraisal with image, navigating to SignIn');
    } catch (error) {
      console.error('Failed to save pending appraisal:', error);
    }
    // Navigate regardless of save success
    navigation.navigate('SignIn', {});
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.formContainer}>
          {/* Item Card */}
          <View style={styles.itemCard}>
            {/* Image */}
            {imageUri && (
              <View style={styles.itemImageContainer}>
                <Image source={{ uri: imageUri }} style={styles.itemImage} />
              </View>
            )}

            <View style={styles.itemContent}>
              {/* Estimated Value Range — hero display */}
              <Text style={commonStyles.sectionLabel}>Estimated Value Range</Text>
              {item.priceRange ? (
                <Text style={styles.valueRange}>
                  ${item.priceRange.min.toFixed(0)} – ${item.priceRange.max.toFixed(0)}
                </Text>
              ) : (
                <Text style={styles.valueRangeEmpty}>Unable to estimate</Text>
              )}

              <Text style={styles.priceSourceLabel}>{priceSourceLabel}</Text>

              {/* Inline Sign-up CTA */}
              <View style={styles.upgradePromptInline}>
                <Ionicons name="trending-up" size={20} color={theme.colors.accent} />
                <Text style={styles.actionPromptInline}>Get more accurate pricing</Text>
                <Text style={styles.upgradeDescriptionInline}>
                  Sign up to access real market data and get valuations based on actual sales.
                </Text>
                <TouchableOpacity
                  style={[commonStyles.primaryButton, styles.inlineSignUpButton]}
                  onPress={handleSignUpWithSave}
                >
                  <Ionicons name="flash" size={16} color={theme.colors.onAccent} />
                  <Text style={commonStyles.primaryButtonText}>Sign Up</Text>
                </TouchableOpacity>
              </View>

              {item.demandScore !== undefined && (
                <View style={styles.marketDemandContainer}>
                  <Text style={commonStyles.inputLabel}>Market Demand</Text>
                  <View style={styles.marketDemandBar}>
                    <View
                      style={[
                        styles.marketDemandFill,
                        { width: `${(item.demandScore || 0) * 100}%` },
                      ]}
                    />
                  </View>
                </View>
              )}

              <View style={styles.fieldGroup}>
                <DetailGroup>
                  <DetailRow label="Brand" value={item.brand || 'N/A'} />
                  <DetailRow label="Model" value={item.model || 'N/A'} />
                  <DetailRow label="Name" value={item.name || 'N/A'} />
                  {item.description ? (
                    <DetailRow label="Description" value={item.description} multiline />
                  ) : null}
                  <DetailRow label="Category" value={item.category || 'N/A'} />
                </DetailGroup>
              </View>

              <View style={styles.fieldGroup}>
                <Text style={commonStyles.inputLabel}>Condition</Text>
                <View style={styles.conditionWrapper}>
                  <ConditionBadge condition={item.condition || 'N/A'} />
                </View>
              </View>
            </View>
          </View>

          {/* Multiple Items Tabs */}
          {items.length > 1 && (
            <View style={styles.selectionHeader}>
              <Text style={styles.selectionCount}>{items.length} items found</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {items.map((i, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.selectAllButton,
                      selectedItemIndex === index && styles.selectAllButtonActive,
                    ]}
                    onPress={() => setSelectedItemIndex(index)}
                  >
                    <Text style={[
                      styles.selectAllText,
                      selectedItemIndex === index && styles.selectAllTextActive,
                    ]}>
                      {i.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}

          {/* Bottom CTA section */}
          <View style={styles.actionSection}>
            <View style={styles.upgradePrompt}>
              <Ionicons name="trending-up" size={24} color={theme.colors.accent} />
              <Text style={styles.actionPrompt}>Get more accurate pricing</Text>
              <Text style={styles.upgradeDescription}>
                Sign up to access real eBay market data and get valuations based on actual sales.
              </Text>
            </View>

            <TouchableOpacity
              style={[commonStyles.primaryButton, styles.fullWidthButton]}
              onPress={handleSignUpWithSave}
            >
              <Ionicons name="flash" size={20} color={theme.colors.onAccent} />
              <Text style={commonStyles.primaryButtonText}>Sign Up for Accurate Pricing</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={commonStyles.link}
              onPress={handleSignInWithSave}
            >
              <Text style={commonStyles.linkText}>
                Already have an account? Sign In
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[commonStyles.secondaryButton, styles.fullWidthButton, styles.scanAgainButton]}
              onPress={() => navigation.navigate('Appraise')}
            >
              <Ionicons name="camera" size={20} color={theme.colors.textPrimary} />
              <Text style={commonStyles.secondaryButtonText}>Scan Another Item</Text>
            </TouchableOpacity>
          </View>

          {/* Disclaimer */}
          <Text style={styles.disclaimer}>
            This is an AI-generated estimate. Sign up for valuations based on real market data.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

